import type { ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { Card } from '../ui/card.js';
import { Skeleton } from '../ui/skeleton.js';

function AppHeaderSkeleton() {
  return (
    <header className="bg-background">
      <div className="mx-auto flex h-16 w-full max-w-[760px] items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Skeleton className="size-8 rounded-full" />
          <Skeleton className="h-8 w-44 rounded-md" />
        </div>
        <Skeleton className="size-8 rounded-full" />
      </div>
    </header>
  );
}

function AppShellSkeletonFrame({ children }: { children: ReactNode }) {
  return (
    <div
      className="grid min-h-dvh grid-rows-[4rem_1fr] bg-background"
      aria-live="polite"
      aria-busy="true"
    >
      <AppHeaderSkeleton />
      <main className="min-w-0 overflow-visible p-4">
        <div className="mx-auto w-full max-w-[720px]">{children}</div>
      </main>
    </div>
  );
}

export function AuthPageSkeleton() {
  return (
    <div className="flex min-h-dvh flex-col items-center gap-10 bg-background pb-8">
      <header className="flex h-16 w-full items-start justify-center pt-4">
        <div className="flex h-8 w-full max-w-[760px] items-center justify-center px-4">
          <div className="flex items-center gap-3">
            <Skeleton className="size-7 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </header>

      <main className="w-full px-4">
        <Card variant="auth" className="mx-auto w-full max-w-sm gap-4">
          <div className="space-y-2">
            <Skeleton className="mx-auto h-5 w-28" />
            <Skeleton className="mx-auto h-4 w-44" />
          </div>
          <Skeleton className="h-11 w-full rounded-lg" />
          <Skeleton className="h-4 w-full" />
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
          <Skeleton className="mx-auto h-4 w-40" />
        </Card>
      </main>
    </div>
  );
}

export function ProtectedAppShellSkeleton() {
  return (
    <AppShellSkeletonFrame>
      <Card variant="form" className="gap-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </div>
      </Card>
    </AppShellSkeletonFrame>
  );
}

export function ProjectCardMetricsSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="rounded-md border border-border bg-muted/20 p-2.5">
          <Skeleton className="mb-2 h-3 w-14" />
          <Skeleton className="h-3.5 w-full" />
        </div>
      ))}
    </div>
  );
}

export function ProjectsHomeGridSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-4 w-64" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} variant="auth" className="gap-4 rounded-xl p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="space-y-2">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-3.5 w-24" />
              </div>
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="h-3.5 w-48" />
            <ProjectCardMetricsSkeleton />
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ProjectsHomePageSkeleton() {
  return (
    <AppShellSkeletonFrame>
      <ProjectsHomeGridSkeleton />
    </AppShellSkeletonFrame>
  );
}

export function DashboardWorkspaceSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[720px]">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
          <Skeleton className="h-10 w-48 rounded-md" />
          <Skeleton className="h-10 w-64 rounded-md md:justify-self-center" />
          <Skeleton className="h-10 w-28 rounded-md md:justify-self-end" />
        </div>

        <Card variant="auth" className="gap-3 rounded-2xl p-5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card variant="auth" className="gap-3 rounded-2xl p-5">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </Card>
          <Card variant="auth" className="gap-3 rounded-2xl p-5">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </Card>
        </div>
      </div>
    </div>
  );
}

export function DashboardConfigContentSkeleton() {
  return (
    <div className="space-y-4">
      <Card variant="auth" className="gap-3 rounded-2xl p-5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card variant="auth" className="gap-3 rounded-2xl p-5">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </Card>
        <Card variant="auth" className="gap-3 rounded-2xl p-5">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      </div>
    </div>
  );
}

export function DashboardSyncWorkspaceSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[720px]">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
          <Skeleton className="h-10 w-48 rounded-md" />
          <Skeleton className="h-10 w-64 rounded-md md:justify-self-center" />
          <Skeleton className="h-10 w-28 rounded-md md:justify-self-end" />
        </div>
        <SyncDashboardSkeleton />
      </div>
    </div>
  );
}

export function DashboardPageSkeleton() {
  return (
    <AppShellSkeletonFrame>
      <DashboardWorkspaceSkeleton />
    </AppShellSkeletonFrame>
  );
}

export function DashboardSyncPageSkeleton() {
  return (
    <AppShellSkeletonFrame>
      <DashboardSyncWorkspaceSkeleton />
    </AppShellSkeletonFrame>
  );
}

export function ProjectSettingsSkeleton() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>

      <Card variant="form" className="gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-10 w-36 rounded-md" />
          <Skeleton className="h-10 w-32 rounded-md" />
        </div>
      </Card>

      <Card variant="form" className="gap-3 border-destructive/30">
        <Skeleton className="h-5 w-28" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-10 w-36 rounded-md" />
      </Card>
    </div>
  );
}

export function SettingsPageSkeleton() {
  return (
    <AppShellSkeletonFrame>
      <ProjectSettingsSkeleton />
    </AppShellSkeletonFrame>
  );
}

export function BillingCardSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-10 w-48 rounded-md" />
    </div>
  );
}

export function AutomationCardsSkeleton() {
  return (
    <div className="grid w-full gap-4 md:grid-cols-2">
      {Array.from({ length: 2 }).map((_, index) => (
        <Card key={index} variant="auth" className="gap-3 p-5">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="size-5 rounded-sm" />
          </div>
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-10 w-full rounded-md" />
          <Skeleton className="h-4 w-40" />
        </Card>
      ))}
    </div>
  );
}

export function SyncDashboardSkeleton() {
  return (
    <section className="space-y-4">
      <div className="relative left-1/2 w-screen -translate-x-1/2 border-b border-border/80">
        <div className="mx-auto w-full max-w-[1300px] px-4 pb-5 pt-7">
          <div className="mx-auto flex w-full max-w-[1300px] flex-col items-center">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-2 h-3.5 w-28" />
            <div className="mt-6 h-48 w-full">
              <Skeleton className="h-full w-full rounded-lg" />
            </div>
            <div className="mt-3 flex w-full justify-end gap-4">
              <Skeleton className="h-3.5 w-14" />
              <Skeleton className="h-3.5 w-28" />
            </div>
            <div className="mt-3 grid w-full grid-cols-[auto_1fr_auto] items-center">
              <Skeleton className="h-3.5 w-28" />
              <div />
              <div className="flex gap-1">
                <Skeleton className="size-6 rounded-md" />
                <Skeleton className="size-6 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card variant="auth" className="gap-3 rounded-2xl p-5">
        <Skeleton className="h-5 w-20" />
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center justify-between gap-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <Card key={index} variant="auth" className="gap-3 rounded-2xl p-5">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </Card>
        ))}
      </div>
    </section>
  );
}

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function TableSkeleton({ rows = 6, columns = 6, className }: TableSkeletonProps) {
  return (
    <Card variant="form" className={cn('gap-3 p-5', className)}>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={`head-${index}`} className="h-4 w-full" />
        ))}
      </div>
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={`cell-${rowIndex}-${colIndex}`} className="h-4 w-full" />
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ApiPreviewSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-3', className)}>
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-3.5 w-64" />
      <Skeleton className="h-56 w-full rounded-lg" />
    </div>
  );
}
